import { useMutation, useQueryClient } from '@tanstack/react-query'

import { patchRequest } from 'src/lib/api-client'
import { queries } from 'src/lib/query-keys'

import type { EnterpriseDetailResponse } from './use-get-enterprise'

export enum LeadDistributionPreference {
  ROUND_ROBIN = 'round_robin',
  RANDOM = 'random',
}

export interface UpdateCompanySettingsRequest {
  position?: string | null
  companySize?: string | null
  companyType?: string | null
  nmlsCompanyIdentifier?: string | null
  primaryFocus?: string[] | null
  totalSpendingLimit?: number | null
  logoPicture?: string | null
  iconPicture?: string | null
  preQualifyWorkflowEnabled?: boolean | null
  introductionPurchaseWorkflowEnabled?: boolean | null
  introductionRefinanceWorkflowEnabled?: boolean | null
  listingAppointmentWorkflowEnabled?: boolean | null
  firstConversationWorkflowEnabled?: boolean | null
  estimateComWorkflowEnabled?: boolean | null
  grcRefinanceWorkflowEnabled?: boolean | null
  leadDistributionPreference?: LeadDistributionPreference | null
}

export interface UpdateCompanySettingsParams {
  enterpriseId: number
  request: UpdateCompanySettingsRequest
}

/** Update enterprise company settings (Owner/Admin only). */
const updateCompanySettings = async ({ enterpriseId, request }: UpdateCompanySettingsParams) => {
  return patchRequest<EnterpriseDetailResponse>(`/enterprise/${enterpriseId}/settings`, request)
}

export default function useUpdateEnterpriseSettings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: UpdateCompanySettingsParams) => {
      const response = await updateCompanySettings(params)

      if (!response) {
        throw new Error('No response received from update company settings')
      }

      queryClient.invalidateQueries({ queryKey: queries.enterprise._def })

      return response
    },
  })
}
